import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StaticHtmlDocumentService } from '../../services/static-html-document.service';
import { HtmlDocType } from '../../common/htmlDocType';
import { CreateOrUpdateStaticHtmlDocumentRequest } from '../../common/createOrUpdateStaticHtmlDocumentRequest';

@Component({
  selector: 'app-document-edit',
  templateUrl: './document-edit.component.html',
  styleUrls: ['./document-edit.component.scss'],
  standalone: false
})
export class DocumentEditComponent implements OnInit {
  isNew = false;
  documentId: number | null = null;
  documentType: HtmlDocType | null = null;
  htmlContent = '';
  loading = true;
  saving = false;
  error: string | null = null;
  typeLabels: Record<HtmlDocType, string> = { [HtmlDocType.PRIVACY]: 'Datenschutz', [HtmlDocType.AGB]: 'AGB', [HtmlDocType.IMPRESSUM]: 'Impressum' };
  readonly editorStyles = { height: '20vw', 'font-family': "'Roboto', sans-serif" };

  constructor(private route: ActivatedRoute, private router: Router, private snackBar: MatSnackBar,
    private documentService: StaticHtmlDocumentService, private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    let documentTypeParam = this.route.snapshot.paramMap.get('documentType');
    let documentIdParam = this.route.snapshot.paramMap.get('documentId');
    if (documentIdParam) {
      this.isNew = false;
      this.documentId = +documentIdParam;
      this.loadDocumentAndCurrentVersion();
    } else if (documentTypeParam && this.isDocumentTypeByValue(+documentTypeParam)) {
      this.isNew = true;
      this.documentType = this.getDocumentTypeByValue(+documentTypeParam);
      this.loading = false;
    } else {
      this.error = 'Ungültige Route.';
      this.loading = false;
    }
  }

  private isDocumentTypeByValue(s: number): boolean {
    return Object.values(HtmlDocType).includes(s);
  }

  loadDocumentAndCurrentVersion(): void {
    if (!this.documentId) return;
    this.documentService.getCurrentVersion(this.documentId!).subscribe({
      next: (version) => {
        this.documentType = version.getDocumentType();
        this.htmlContent = version.getHtmlContent() || '';
        this.loading = false;
        this.cd.detectChanges();
      },
      error: (err) => {
        this.error = err?.error?.message || 'Aktuelle Version konnte nicht geladen werden.';
        this.loading = false;
        this.cd.detectChanges();
      }
    });
  }

  get titleLabel(): string {
    if (this.isNew && this.documentType != null) {
      return `${this.typeLabels[this.documentType]} anlegen`;
    }
    if (this.documentType != null) {
      return `${this.typeLabels[this.documentType]} bearbeiten`;
    }
    return 'Dokument bearbeiten';
  }

  save(): void {
    if (this.documentType == null || !this.htmlContent.trim()) {
      this.snackBar.open('Bitte Inhalt eingeben.', 'OK', { duration: 3000 });
      return;
    }
    let request: CreateOrUpdateStaticHtmlDocumentRequest = new CreateOrUpdateStaticHtmlDocumentRequest(this.documentType, this.htmlContent.trim());
    this.saving = true;
    this.error = null;
    if (this.isNew) {
      this.documentService.createDocument(request).subscribe({
        next: (doc) => {
          this.saving = false;
          this.snackBar.open('Dokument angelegt. Erste Version wurde gespeichert.', 'OK', { duration: 4000 });
          this.router.navigate(['overview'], { relativeTo: this.route.parent });
        },
        error: (err) => {
          this.error = err?.error?.message || 'Speichern fehlgeschlagen.';
          this.saving = false;
          this.cd.detectChanges();
        }
      });
    } else if (this.documentId) {
      this.documentService.updateDocument(this.documentId, request).subscribe({
        next: () => {
          this.saving = false;
          this.snackBar.open('Neue Version gespeichert. Vorherige Versionen bleiben unverändert.', 'OK', { duration: 4000 });
          this.router.navigate(['overview'], { relativeTo: this.route.parent });
        },
        error: (err) => {
          this.error = err?.error?.message || 'Speichern fehlgeschlagen.';
          this.saving = false;
          this.cd.detectChanges();
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['overview'], { relativeTo: this.route.parent });
  }

  private getDocumentTypeByValue(s: number): HtmlDocType {
    return s as HtmlDocType;
  }
}
